import { Shell } from "@/components/layout/Shell";
import { useSeo } from "@/hooks/use-seo";
import { AdSlot } from "@/components/ads/AdSlot";
import { useState, useEffect, FormEvent, useRef } from "react";
import { Link } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { COLOR_LIST } from "@/data/colors";
import { Download, FileJson, FileSpreadsheet, Code2, Image } from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────────────────

type Context = "text" | "background" | "border" | "shadow" | "fill" | "other";

interface ExtractedColor {
  hex: string;       // 6-digit lowercase, no #
  count: number;
  contexts: Context[];
}

interface ScanResult {
  url: string;
  pageTitle: string;
  colorCount: number;
  colors: ExtractedColor[];
  scanMethod: "css" | "visual";
  scannedAt: string;
}

// ── Export helpers ─────────────────────────────────────────────────────────────

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function safeFilename(urlStr: string) {
  try {
    return new URL(urlStr).hostname.replace(/[^a-z0-9.-]/gi, "_");
  } catch {
    return "palette";
  }
}

function exportJson(result: ScanResult) {
  const data = {
    url: result.url,
    pageTitle: result.pageTitle,
    scanMethod: result.scanMethod,
    scannedAt: result.scannedAt,
    colorCount: result.colorCount,
    colors: result.colors.map((ec) => {
      const { r, g, b } = hexToRgb(ec.hex);
      const { h, s, l } = rgbToHsl(r, g, b);
      const { color: match } = findNearestLibraryColor(ec.hex);
      return {
        hex: `#${ec.hex.toUpperCase()}`,
        rgb: { r, g, b },
        hsl: { h, s, l },
        contexts: ec.contexts,
        usageCount: ec.count,
        psychologyMatch: { name: match.name, meaning: match.meaning },
      };
    }),
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  downloadBlob(blob, `${safeFilename(result.url)}-palette.json`);
}

function exportCsv(result: ScanResult) {
  const rows = [
    ["HEX", "R", "G", "B", "H", "S%", "L%", "Contexts", "Usage count", "Psychology match"],
    ...result.colors.map((ec) => {
      const { r, g, b } = hexToRgb(ec.hex);
      const { h, s, l } = rgbToHsl(r, g, b);
      const { color: match } = findNearestLibraryColor(ec.hex);
      return [
        `#${ec.hex.toUpperCase()}`,
        r, g, b, h, s, l,
        ec.contexts.join(" | "),
        ec.count,
        match.name,
      ];
    }),
  ];
  const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  downloadBlob(blob, `${safeFilename(result.url)}-palette.csv`);
}

function exportCss(result: ScanResult) {
  const vars = result.colors
    .map((ec, i) => `  --color-${i + 1}: #${ec.hex.toUpperCase()};`)
    .join("\n");
  const comment = `/* ${result.pageTitle || result.url} — extracted by Mychromamind */\n`;
  const blob = new Blob([comment + `:root {\n${vars}\n}\n`], { type: "text/css" });
  downloadBlob(blob, `${safeFilename(result.url)}-palette.css`);
}

function exportPng(result: ScanResult) {
  const colors = result.colors.slice(0, 30);
  const COLS = 5;
  const ROWS = Math.ceil(colors.length / COLS);
  const SW = 112, SH = 80, PAD = 16, HEADER = 56, FOOTER = 40;
  const W = COLS * SW + PAD * 2;
  const H = HEADER + ROWS * SH + PAD + FOOTER;

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  // Background
  ctx.fillStyle = "#FBF9F4";
  ctx.fillRect(0, 0, W, H);

  // Header
  ctx.fillStyle = "#1a1a1a";
  ctx.font = "bold 15px sans-serif";
  ctx.fillText(result.pageTitle || result.url, PAD, PAD + 18);
  ctx.fillStyle = "#888";
  ctx.font = "12px sans-serif";
  ctx.fillText(`${result.colorCount} colours · ${result.scanMethod} scan · mychromamind.com`, PAD, PAD + 38);

  // Swatches
  colors.forEach((ec, idx) => {
    const col = idx % COLS;
    const row = Math.floor(idx / COLS);
    const x = PAD + col * SW;
    const y = HEADER + row * SH;
    const R = 8;

    // Swatch
    ctx.fillStyle = `#${ec.hex}`;
    ctx.beginPath();
    ctx.roundRect(x + 4, y + 4, SW - 8, SH - 24, R);
    ctx.fill();

    // Hex label
    ctx.fillStyle = "#333";
    ctx.font = "10px monospace";
    ctx.fillText(`#${ec.hex.toUpperCase()}`, x + 4, y + SH - 8);
  });

  // Footer
  ctx.fillStyle = "#aaa";
  ctx.font = "11px sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("mychromamind.com", W / 2, H - 14);

  canvas.toBlob((blob) => {
    if (blob) downloadBlob(blob, `${safeFilename(result.url)}-palette.png`);
  }, "image/png");
}

const EXPORT_OPTIONS = [
  { id: "json", label: "JSON",          sub: "Developer data",  Icon: FileJson },
  { id: "csv",  label: "CSV",           sub: "Spreadsheets",    Icon: FileSpreadsheet },
  { id: "css",  label: "CSS Variables", sub: "Web design",      Icon: Code2 },
  { id: "png",  label: "Palette image", sub: "PNG swatch card", Icon: Image },
] as const;

type ExportId = typeof EXPORT_OPTIONS[number]["id"];

function ExportMenu({ result }: { result: ScanResult }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  function run(id: ExportId) {
    setOpen(false);
    if (id === "json") exportJson(result);
    if (id === "csv")  exportCsv(result);
    if (id === "css")  exportCss(result);
    if (id === "png")  exportPng(result);
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border bg-card text-sm font-medium hover:bg-accent transition-colors"
      >
        <Download className="w-4 h-4" />
        Export
      </button>

      <AnimatePresence>
        {open && (
          <>
            {/* backdrop */}
            <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -4 }}
              transition={{ duration: 0.12 }}
              className="absolute right-0 top-full mt-2 w-52 rounded-2xl border border-border bg-card shadow-xl z-20 overflow-hidden"
            >
              {EXPORT_OPTIONS.map(({ id, label, sub, Icon }) => (
                <button
                  key={id}
                  onClick={() => run(id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-accent transition-colors"
                >
                  <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{label}</p>
                    <p className="text-[11px] text-muted-foreground">{sub}</p>
                  </div>
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── SEO copy ───────────────────────────────────────────────────────────────────

const HOW_IT_WORKS = `Mychromamind's Website Scanner uses a two-stage approach to extract color palettes from any public website. In the first stage — the CSS fast path — the scanner fetches the page's raw HTML and parses every color value it finds in inline style tags, style attributes, and linked external stylesheets. This is fast (typically under a second) and precise, because it reads the colors exactly as the designer wrote them. In the second stage, if the CSS path returns fewer than three colors — for example because the site uses a CDN that blocks bot requests, relies heavily on JavaScript rendering, or is protected by Cloudflare — the scanner automatically launches a headless Chromium browser, navigates to the page like a real visitor, waits for the page to fully render, and takes a screenshot. It then samples thousands of pixels from that screenshot and classifies them into human color names using the same perceptual HSL bucketing algorithm as Color Grabber. The result is a real color palette extracted from what a human would actually see — not just what appears in the CSS source. Each color is then matched against the Mychromamind psychology library so you can see not just hex values, but what each color communicates emotionally. Every URL is validated against private IP ranges before scanning, so only publicly accessible sites are supported.`;

// ── Colour utilities ───────────────────────────────────────────────────────────

function hexToRgb(hex: string) {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgbToHsl(r: number, g: number, b: number) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function contrastColor(hex: string): string {
  const { r, g, b } = hexToRgb(hex);
  const lum = 0.2126 * r / 255 + 0.7152 * g / 255 + 0.0722 * b / 255;
  return lum > 0.45 ? "#000000" : "#ffffff";
}

function colorDistance(hex1: string, hex2: string): number {
  const a = hexToRgb(hex1), b = hexToRgb(hex2);
  return Math.sqrt((a.r - b.r) ** 2 + (a.g - b.g) ** 2 + (a.b - b.b) ** 2);
}

function findNearestLibraryColor(hex: string) {
  let best = COLOR_LIST[0];
  let bestDist = Infinity;
  for (const c of COLOR_LIST) {
    const d = colorDistance(hex, c.hex.replace("#", ""));
    if (d < bestDist) { bestDist = d; best = c; }
  }
  return { color: best, distance: Math.round(bestDist) };
}

const CONTEXT_LABELS: Record<Context, string> = {
  text: "Text",
  background: "Background",
  border: "Border",
  shadow: "Shadow",
  fill: "Fill / SVG",
  other: "Other",
};

const CONTEXT_COLORS: Record<Context, string> = {
  text:       "bg-blue-100 text-blue-700",
  background: "bg-amber-100 text-amber-700",
  border:     "bg-slate-100 text-slate-600",
  shadow:     "bg-purple-100 text-purple-700",
  fill:       "bg-green-100 text-green-700",
  other:      "bg-gray-100 text-gray-600",
};

// ── Subcomponents ─────────────────────────────────────────────────────────────

function ColorCard({ ec, index }: { ec: ExtractedColor; index: number }) {
  const [copied, setCopied] = useState(false);
  const fullHex = `#${ec.hex}`;
  const { r, g, b } = hexToRgb(ec.hex);
  const { h, s, l } = rgbToHsl(r, g, b);
  const fg = contrastColor(ec.hex);
  const { color: match, distance } = findNearestLibraryColor(ec.hex);
  const isClose = distance < 80;

  function copy() {
    navigator.clipboard.writeText(fullHex).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03, duration: 0.3 }}
      className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm hover:shadow-md transition-shadow"
    >
      {/* Swatch */}
      <div
        className="h-28 flex items-end justify-between px-4 py-3 cursor-pointer select-none"
        style={{ backgroundColor: fullHex, color: fg }}
        onClick={copy}
        title="Click to copy hex"
      >
        <span className="font-mono text-sm font-semibold tracking-wide opacity-90">
          {fullHex.toUpperCase()}
        </span>
        <span className="text-xs opacity-70">
          {copied ? "Copied ✓" : "Copy"}
        </span>
      </div>

      {/* Details */}
      <div className="p-4 space-y-3">
        {/* RGB / HSL */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs font-mono text-muted-foreground">
          <span>rgb({r}, {g}, {b})</span>
          <span>hsl({h}, {s}%, {l}%)</span>
        </div>

        {/* Contexts */}
        {ec.contexts.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {ec.contexts.map((ctx) => (
              <span
                key={ctx}
                className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${CONTEXT_COLORS[ctx]}`}
              >
                {CONTEXT_LABELS[ctx]}
              </span>
            ))}
          </div>
        )}

        {/* Frequency */}
        <p className="text-xs text-muted-foreground">
          Used <span className="font-semibold text-foreground">{ec.count}</span>{" "}
          {ec.count === 1 ? "time" : "times"} in CSS
        </p>

        {/* Library match */}
        <div className="pt-2 border-t border-border/60">
          <p className="text-[10px] text-muted-foreground mb-1.5 uppercase tracking-wide font-medium">
            Closest psychology match
          </p>
          <Link
            href={`/learn/${match.id}`}
            className="flex items-center gap-2.5 group"
          >
            <span
              className="w-6 h-6 rounded-full flex-shrink-0 border border-border/30"
              style={{ backgroundColor: match.hex }}
            />
            <div className="min-w-0">
              <p className="text-sm font-medium group-hover:text-primary transition-colors truncate">
                {match.name}
              </p>
              {!isClose && (
                <p className="text-[10px] text-muted-foreground">approximate</p>
              )}
            </div>
            <span className="ml-auto text-muted-foreground text-xs group-hover:text-primary transition-colors">→</span>
          </Link>
          <p className="text-[10px] text-muted-foreground mt-1.5 line-clamp-2">
            {match.meaning}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

/** Small pill badge indicating which scan method was used */
function ScanMethodBadge({ method }: { method: "css" | "visual" }) {
  if (method === "visual") {
    return (
      <span
        title="This site blocked CSS extraction, so the scanner used a headless browser screenshot to detect colors visually."
        className="inline-flex items-center gap-1 text-[10px] font-medium px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 border border-violet-200 cursor-help"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-violet-500 inline-block" />
        Visual scan
      </span>
    );
  }
  return (
    <span
      title="Colors were extracted directly from the site's CSS — fast and precise."
      className="inline-flex items-center gap-1 text-[10px] font-medium px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 cursor-help"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
      CSS scan
    </span>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

type Status = "idle" | "loading" | "success" | "error";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export default function Scan() {
  useSeo({
    title: "Website Color Scanner | Extract Any Site's Palette — Mychromamind",
    description:
      "Instantly extract every colour used on any website — with psychology meanings, RGB/HSL values, and usage context. Works even on Cloudflare-protected and JS-rendered sites.",
    path: "/scan",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "SoftwareApplication",
          name: "Website Color Scanner",
          applicationCategory: "DesignApplication",
          operatingSystem: "Web",
          description:
            "Extract every colour from any public website — CSS-based for standard sites, Playwright visual scan for JS-rendered and bot-protected pages. Includes psychology meanings, hex, RGB, and HSL values.",
          url: "https://mychromamind.com/scan",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home",    item: "https://mychromamind.com" },
            { "@type": "ListItem", position: 2, name: "Scanner", item: "https://mychromamind.com/scan" },
          ],
        },
      ],
    },
  });

  const [url, setUrl]       = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [result, setResult] = useState<ScanResult | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  // Auto-trigger a scan when the page is opened with ?url=... from the homepage.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const u = params.get("url");
    if (!u) return;
    setUrl(u);
    setStatus("loading");
    fetch(`${API_BASE}/api/color-scan`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ url: u }),
    })
      .then(async (res) => {
        const data = await res.json() as { error?: string } & ScanResult;
        if (!res.ok) { setErrorMsg(data.error ?? "Something went wrong."); setStatus("error"); return; }
        setResult(data);
        setStatus("success");
      })
      .catch(() => { setErrorMsg("Could not reach the scanner. Please try again."); setStatus("error"); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!url.trim()) return;

    setStatus("loading");
    setResult(null);
    setErrorMsg("");

    try {
      const res = await fetch(`${API_BASE}/api/color-scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: url.trim() }),
      });

      const data = await res.json() as { error?: string } & ScanResult;

      if (!res.ok) {
        setErrorMsg(data.error ?? "Something went wrong. Please try again.");
        setStatus("error");
        return;
      }

      setResult(data);
      setStatus("success");
    } catch {
      setErrorMsg("Could not reach the scanner. Please try again.");
      setStatus("error");
    }
  }

  return (
    <Shell>
      <div className="max-w-5xl mx-auto px-5 sm:px-6 py-10 md:py-20 w-full">

        {/* Hero */}
        <div className="max-w-2xl mb-10">
          <h1 className="text-4xl md:text-5xl font-serif mb-4 leading-tight">
            Website Scanner
          </h1>
          <p className="text-muted-foreground text-lg">
            Enter any URL to extract every colour used on that site — complete with
            psychology meanings, RGB / HSL values, and usage context. Works on standard sites
            and on Cloudflare-protected or JavaScript-rendered pages. Built for designers,
            brand teams, and the colour-curious.
          </p>
        </div>

        {/* Input form */}
        <form onSubmit={handleSubmit} className="mb-10">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none select-none">
                🔍
              </span>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com"
                required
                autoComplete="url"
                spellCheck={false}
                className="w-full pl-10 pr-4 py-3.5 rounded-xl border border-border bg-card text-sm placeholder:text-muted-foreground/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-shadow"
              />
            </div>
            <button
              type="submit"
              disabled={status === "loading"}
              className="px-7 py-3.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {status === "loading" ? "Scanning…" : "Scan site"}
            </button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            URLs are validated for safety before scanning. Only publicly accessible sites are supported.
          </p>
        </form>

        {/* Loading */}
        <AnimatePresence>
          {status === "loading" && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-24 gap-5 text-muted-foreground"
            >
              <div className="flex gap-1.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <motion.div
                    key={i}
                    className="w-3 h-3 rounded-full bg-primary"
                    animate={{ scale: [1, 1.5, 1], opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 0.9, delay: i * 0.15, repeat: Infinity }}
                  />
                ))}
              </div>
              <p className="text-sm">Scanning… this may take up to 30 s for complex sites.</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error */}
        {status === "error" && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-5 rounded-2xl border border-destructive/30 bg-destructive/5 text-sm text-destructive mb-8"
            role="alert"
          >
            <strong className="font-semibold">Could not scan this URL.</strong>{" "}
            {errorMsg}
          </motion.div>
        )}

        {/* Results */}
        {status === "success" && result && (
          <motion.div
            key="results"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Summary bar */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-6 pb-6 border-b border-border/50">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-0.5">Page</p>
                <p className="text-sm font-medium truncate max-w-sm">
                  {result.pageTitle || result.url}
                </p>
                <a
                  href={result.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground hover:text-primary transition-colors truncate max-w-xs block"
                >
                  {result.url}
                </a>
              </div>
              <div className="sm:ml-auto flex items-center gap-4">
                <div className="text-center">
                  <p className="text-2xl font-serif font-medium text-primary">{result.colorCount}</p>
                  <p className="text-xs text-muted-foreground">colours found</p>
                </div>
                <ScanMethodBadge method={result.scanMethod ?? "css"} />
                <ExportMenu result={result} />
              </div>
            </div>

            {result.colors.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground text-sm">
                No colours were found on this page. The site may be using images or
                canvas-based rendering instead of CSS.
              </div>
            ) : (
              <>
                {/* Palette strip */}
                <div className="flex flex-wrap gap-1 mb-8">
                  {result.colors.slice(0, 30).map((ec) => (
                    <div
                      key={ec.hex}
                      className="w-8 h-8 rounded-md flex-shrink-0 border border-border/20 cursor-pointer hover:scale-110 transition-transform"
                      style={{ backgroundColor: `#${ec.hex}` }}
                      title={`#${ec.hex.toUpperCase()}`}
                    />
                  ))}
                </div>

                {/* Card grid */}
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  {result.colors.map((ec, i) => (
                    <ColorCard key={ec.hex} ec={ec} index={i} />
                  ))}
                </div>

                <p className="mt-8 text-xs text-muted-foreground text-center">
                  Showing up to 60 colours sorted by frequency of use. Psychology matches
                  are the closest colour from the Mychromamind 175-colour library.
                </p>
              </>
            )}

            {/* Ad slot — only renders when HOW_IT_WORKS clears the 120-word guard */}
            <AdSlot
              slotId="2431849490"
              content={[HOW_IT_WORKS, result.colors.length > 0 ? "Colors found." : ""]}
              className="mt-10"
            />

            {/* How it works — substantive body copy for SEO + AdSense content policy */}
            <div className="mt-10 p-6 rounded-2xl border border-border bg-card/60">
              <h2 className="text-lg font-serif font-medium mb-3">How the Scanner Works</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {HOW_IT_WORKS}
              </p>
            </div>
          </motion.div>
        )}

        {/* Idle tip */}
        {status === "idle" && (
          <>
            <div className="grid sm:grid-cols-3 gap-4 mt-4">
              {[
                { icon: "🛡️", title: "Security first", body: "Every URL is validated and checked against private IP ranges before scanning. Only public websites are allowed." },
                { icon: "🎨", title: "Psychology layer", body: "Each extracted colour is matched to the Mychromamind library — so you see not just hex values but what each colour communicates." },
                { icon: "🌐", title: "Two-stage scan", body: "CSS extraction runs first for instant results. If the site blocks bots, a headless browser takes a screenshot so you still get the full palette." },
              ].map(({ icon, title, body }) => (
                <div key={title} className="p-5 rounded-2xl border border-border bg-card">
                  <div className="text-2xl mb-2">{icon}</div>
                  <h3 className="font-medium text-sm mb-1">{title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{body}</p>
                </div>
              ))}
            </div>

            {/* How it works — visible at idle too for SEO */}
            <div className="mt-8 p-6 rounded-2xl border border-border bg-card/60">
              <h2 className="text-lg font-serif font-medium mb-3">How the Scanner Works</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {HOW_IT_WORKS}
              </p>
            </div>

            {/* Ad slot on idle state — HOW_IT_WORKS alone is 200+ words */}
            <AdSlot
              slotId="2431849490"
              content={[HOW_IT_WORKS]}
              className="mt-8"
            />
          </>
        )}
      </div>
    </Shell>
  );
}
